
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Typography,
} from "antd";

import personaApi from "../persona/persona.api";
import usageAnalyticsApi from "./usageAnalytics.api";

const { Title, Paragraph, Text } = Typography;

const reportingPeriods = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const UsageAnalytics = () => {
  const [days, setDays] = useState(30);
  const [userSearch, setUserSearch] = useState("");
  const [refreshCount, setRefreshCount] = useState(0);
  const [report, setReport] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const loadReport = async () => {
      setLoading(true);
      setError("");
      setReport(null);

      try {
        const [usageResult, personaResult] =
          await Promise.allSettled([
            usageAnalyticsApi.getUsageSummary(days),
            personaApi.getPersonas(),
          ]);

        if (!active) return;

        if (usageResult.status !== "fulfilled") {
          setError("Unable to load usage analytics. Please try again.");
          return;
        }

        setReport(usageResult.value.data);

        setPersonas(
          personaResult.status === "fulfilled" &&
            Array.isArray(personaResult.value.data)
            ? personaResult.value.data
            : []
        );
      } catch (err) {
        if (active) {
          setError("Unable to load usage analytics. Please try again.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadReport();

    return () => {
      active = false;
    };
  }, [days, refreshCount]);

  const companionNames = useMemo(() => {
    return new Map(
      personas.map((persona) => [
        String(persona._id),
        persona.name,
      ])
    );
  }, [personas]);

  const companionRows = (report?.companionExchanges || []).map(
    (item) => ({
      ...item,
      companionName:
        companionNames.get(String(item.companionId)) ||
        "Unavailable companion",
    })
  );

  const activeHours = (report?.hourlyExchangesUtc || []).filter(
    (item) => item.exchanges > 0
  );

  return (
    <div className="page-shell">
      <div className="page-heading-row">
        <Title level={1}>Usage Analytics</Title>
      </div>

      <Paragraph className="passcodes-intro">
        Content-free usage statistics. No conversation text,
        audio or original passcodes are included.
      </Paragraph>

      <div className="page-toolbar" style={{ marginTop: 18 }}>
        <Select
          aria-label="Reporting period"
          value={days}
          options={reportingPeriods}
          onChange={setDays}
          style={{ minWidth: 180 }}
        />

        <Button
          onClick={() => setRefreshCount((count) => count + 1)}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 18 }}
        />
      )}

      {loading && (
        <div style={{ padding: 32, textAlign: "center" }}>
          <Spin />
        </div>
      )}

      {!loading && report && (
        <>
          <Paragraph type="secondary">
            Reporting period: {report.period.fromDate} to{" "}
            {report.period.toDate} (UTC)
          </Paragraph>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card size="small">
                <Statistic
                  title="Voice exchanges"
                  value={report.totalExchanges}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card size="small">
                <Statistic
                  title="Active users"
                  value={report.activeUsers}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card size="small">
                <Statistic
                  title="Estimated sessions"
                  value={report.estimatedSessions}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card size="small">
                <Statistic
                  title="Average observed session span"
                  value={
                    report.averageObservedSpanMinutes === null
                      ? "Unknown"
                      : report.averageObservedSpanMinutes
                  }
                  suffix={
                    report.averageObservedSpanMinutes === null
                      ? null
                      : "min"
                  }
                />
              </Card>
            </Col>
          </Row>

          <Paragraph
            style={{ marginTop: 18, marginBottom: 20 }}
          >
            <Text type="secondary">
              Sessions are estimated using a 30-minute inactivity
              gap for each user and companion.{" "}
              {report.singleExchangeSessions} single-exchange
              session(s) have an unknown duration. The average
              observed span excludes these sessions and does not

              measure exact conversation time.
            </Text>
          </Paragraph>

          <Card
            title="Usage by User"
            size="small"
            style={{ marginBottom: 20 }}
          >
            <Input.Search
              placeholder="Search users by name or ID"
              aria-label="Search users by name or ID"
              allowClear
              value={userSearch}
              onChange={(event) => setUserSearch(event.target.value)}
              style={{
                width: "100%",
                maxWidth: 360,
                marginBottom: 16,
              }}
            />

            <Table
              size="small"
              rowKey="userId"
              dataSource={(report.usageByUser || []).filter((user) =>
                `${user.userName || ""} ${user.userId || ""}`
                  .toLowerCase()
                  .includes(userSearch.trim().toLowerCase())
              )}
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
              }}
              scroll={{ x: 800 }}
              columns={[
                {
                  title: "User",
                  dataIndex: "userName",
                  render: (name, user) => (
                    <div>
                      <div>{name || "Unnamed user"}</div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        User ID: {user.userId.slice(-6)}
                      </Text>
                    </div>
                  ),
                },
                {
                  title: "Exchanges",
                  dataIndex: "exchanges",
                  align: "right",
                },
                {
                  title: "Sessions",
                  dataIndex: "estimatedSessions",
                  align: "right",
                },
                {
                  title: "Average observed span",
                  dataIndex: "averageObservedSpanMinutes",
                  align: "right",
                  render: (minutes) =>
                    minutes === null ? "Unknown" : `${minutes} min`,
                },
                {
                  title: "Last active (UK)",
                  dataIndex: "lastActiveAt",
                  render: (value) =>
                    new Date(value).toLocaleString("en-GB", {
                      timeZone: "Europe/London",
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                },
              ]}
              locale={{
                emptyText: userSearch.trim()
                  ? "No matching users."
                  : "No user activity recorded.",
              }}
            />
          </Card>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Usage by companion" size="small">
                <Table
                  size="small"
                  rowKey="companionId"
                  dataSource={companionRows}
                  pagination={false}
                  columns={[
                    {
                      title: "Companion",
                      dataIndex: "companionName",
                    },
                    {
                      title: "Exchanges",
                      dataIndex: "exchanges",
                      align: "right",
                    },
                  ]}
                  locale={{
                    emptyText: "No usage recorded.",
                  }}
                />
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title="Daily activity (UTC)" size="small">
                <Table
                  size="small"
                  rowKey="date"
                  dataSource={report.dailyExchanges}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: false,
                  }}
                  columns={[
                    {
                      title: "Date",
                      dataIndex: "date",
                      render: (date) =>
                        new Date(
                          `${date}T00:00:00Z`
                        ).toLocaleDateString("en-GB", {
                          dateStyle: "medium",
                          timeZone: "UTC",
                        }),
                    },
                    {
                      title: "Exchanges",
                      dataIndex: "exchanges",
                      align: "right",
                    },
                  ]}
                  locale={{
                    emptyText: "No usage recorded.",
                  }}
                />
              </Card>
            </Col>

            <Col xs={24}>
              <Card title="Activity by hour (UTC)" size="small">
                <Table
                  size="small"
                  rowKey="hour"
                  dataSource={activeHours}
                  pagination={false}
                  columns={[
                    {
                      title: "Hour",
                      dataIndex: "hour",
                      render: (hour) =>
                        `${String(hour).padStart(2, "0")}:00 UTC`,
                    },
                    {
                      title: "Exchanges",
                      dataIndex: "exchanges",
                      align: "right",
                    },
                  ]}
                  locale={{
                    emptyText: "No usage recorded.",
                  }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default UsageAnalytics;
